import React, { useState } from 'react';
import { ModalWrapper } from '../../../components/ModalWrapper';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/free-solid-svg-icons';
import { DarkMode, BG_COLOR } from '../types/darkMode';

interface IProps {
  imageUrl: string;
  vocId: number;
  vocTitle: string;
  imageType: 'original' | 'alternative_1' | 'alternative_2';
  onClose: () => void;
}

export const ImageViewerModal: React.FC<IProps> = ({ imageUrl, vocId, vocTitle, imageType, onClose }) => {
  const [darkMode, setDarkMode] = useState(DarkMode.LIGHT);

  const toggleDarkMode = () => {
    setDarkMode((d) => (d === DarkMode.LIGHT ? DarkMode.DARK : DarkMode.LIGHT));
  };

  const getImageTypeLabel = () => {
    switch (imageType) {
      case 'original':
        return 'Original PNG';
      case 'alternative_1':
        return 'Alternative 1';
      case 'alternative_2':
        return 'Alternative 2';
      default:
        return 'Image';
    }
  };

  return (
    <ModalWrapper onClose={onClose} title={`${getImageTypeLabel()} - ${vocTitle}`} width={600} height={500}>
      <ContentWrapper>
        <MetadataSection>
          <MetadataItem>
            <strong>Vocabulary ID:</strong> {vocId}
          </MetadataItem>
          <MetadataItem>
            <strong>Title:</strong> {vocTitle}
          </MetadataItem>
          <MetadataItem>
            <strong>Type:</strong> {getImageTypeLabel()}
          </MetadataItem>
        </MetadataSection>

        <ImageContainer $bgColor={BG_COLOR[darkMode]}>
          <StyledImage
            src={imageUrl}
            alt={`${vocTitle} - ${getImageTypeLabel()}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              if (target.parentElement) {
                target.parentElement.innerHTML = "<div style='color: #999; text-align: center;'>Image not found</div>";
              }
            }}
          />
          <ToggleButton onClick={toggleDarkMode} title="Toggle light/dark background">
            <FontAwesomeIcon icon={faMoon} />
          </ToggleButton>
        </ImageContainer>
      </ContentWrapper>
    </ModalWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`;

const MetadataSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const MetadataItem = styled.div`
  font-size: 14px;
  color: #333;

  strong {
    color: #000;
    margin-right: 8px;
  }
`;

const ImageContainer = styled.div<{ $bgColor: string }>`
  width: 500px;
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.$bgColor};
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 auto;

  &:hover button {
    opacity: 1;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  color: #607d8b;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  opacity: 0;

  &:hover {
    color: #455a64;
    background-color: #fff;
  }
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;
